import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Plus, Pencil, Trash2, X, Loader2, Package, Upload } from "lucide-react";
import { toast } from "sonner";
import ExcelImportModal from "../components/ExcelImportModal";
import { useAuth } from "../context/AuthContext";
import { canDoAction } from "../lib/menu";

const BLANK = {
  nome: "", sku: "", categoria: "", sottocategoria: "",
  costo_unitario: 0, quantita_virtuale: 0, quantita_fisica: 0, scorta_minima: 5,
  descrizione: "", fornitore: "", commessa_ids: [], attivo: true,
};

export default function Prodotti() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [categorie, setCategorie] = useState([]);
  const [showImport, setShowImport] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get("/prodotti"),
        api.get("/commesse"),
        api.get("/constants"),
      ]);
      setList(a.data);
      setCommesse(b.data);
      setCategorie(c.data.materiali_categorie || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(BLANK); setShowForm(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...BLANK, ...p }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        costo_unitario: Number(form.costo_unitario) || 0,
        quantita_virtuale: Number(form.quantita_virtuale) || 0,
        quantita_fisica: Number(form.quantita_fisica) || 0,
        scorta_minima: Number(form.scorta_minima) || 5,
      };
      if (editing) await api.put(`/prodotti/${editing.id}`, payload);
      else await api.post("/prodotti", payload);
      toast.success(editing ? "Prodotto aggiornato" : "Prodotto creato");
      setShowForm(false); await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare ${p.nome}?`)) return;
    try { await api.delete(`/prodotti/${p.id}`); toast.success("Eliminato"); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleCommessa = (id) => {
    const exists = form.commessa_ids.includes(id);
    setForm({
      ...form,
      commessa_ids: exists ? form.commessa_ids.filter((x) => x !== id) : [...form.commessa_ids, id],
    });
  };

  return (
    <div data-testid="prodotti-page">
      <div className="flex justify-between items-end mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Prodotti</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
            {list.length} prodotti in magazzino
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImport(true)}
            className="btn-secondary"
            data-testid="import-prodotti-btn"
            title="Importa prodotti da Excel"
          >
            <Upload className="w-4 h-4" /> Importa Excel
          </button>
          <button onClick={openNew} className="btn-primary" data-testid="new-prodotto-btn">
            <Plus className="w-4 h-4" /> Nuovo prodotto
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-8 flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>
      ) : list.length === 0 ? (
        <div className="card-shell p-12 text-center">
          <Package className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
          <div className="font-semibold">Nessun prodotto</div>
          <button onClick={openNew} className="btn-primary mt-4">Aggiungi prodotto</button>
        </div>
      ) : (
        <div className="card-shell overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ background: "var(--surface-muted)" }}>
                <tr>
                  {["Nome", "SKU", "Categoria", "Costo €", "Q.tà Virt.", "Q.tà Fis.", "Commesse", ""].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.map((p) => (
                  <tr key={p.id} className="border-t hover:bg-[var(--surface-muted)]" style={{ borderColor: "var(--border)" }} data-testid={`prodotto-row-${p.id}`}>
                    <td className="px-4 py-3 font-semibold">{p.nome}</td>
                    <td className="px-4 py-3 font-mono text-xs" style={{ color: "var(--text-muted)" }}>{p.sku || "—"}</td>
                    <td className="px-4 py-3 capitalize">{(p.categoria || "—").replace(/_/g, " ")}</td>
                    <td className="px-4 py-3">€ {Number(p.costo_unitario || 0).toFixed(2)}</td>
                    <td className="px-4 py-3">{p.quantita_virtuale}</td>
                    <td className="px-4 py-3">{p.quantita_fisica}</td>
                    <td className="px-4 py-3 text-xs">{(p.commessa_ids || []).length} associate</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEdit(p)} className="p-1.5 rounded hover:bg-black/5"><Pencil className="w-4 h-4" /></button>
                      {canDoAction(user, "prodotti_delete") && (
                        <button onClick={() => remove(p)} className="p-1.5 rounded hover:bg-black/5 ml-1"><Trash2 className="w-4 h-4" style={{ color: "#B30000" }} /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/40 overflow-y-auto" onClick={() => setShowForm(false)}>
          <div className="card-shell w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b flex justify-between items-center" style={{ borderColor: "var(--border)" }}>
              <h2 className="text-xl font-bold" style={{ fontFamily: "Manrope" }}>{editing ? "Modifica prodotto" : "Nuovo prodotto"}</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded hover:bg-black/5"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={save} className="p-6 grid md:grid-cols-2 gap-4">
              <Field label="Nome *" val={form.nome} on={(v) => setForm({ ...form, nome: v })} req testid="p-nome" />
              <Field label="SKU" val={form.sku} on={(v) => setForm({ ...form, sku: v })} testid="p-sku" />
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Categoria</label>
                <select data-testid="p-categoria" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }}>
                  <option value="">—</option>
                  {categorie.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                </select>
              </div>
              <Field label="Sottocategoria" val={form.sottocategoria} on={(v) => setForm({ ...form, sottocategoria: v })} testid="p-sottocat" />
              <Field label="Costo unitario €" type="number" step="0.01" val={form.costo_unitario} on={(v) => setForm({ ...form, costo_unitario: v })} testid="p-costo" />
              <Field label="Fornitore" val={form.fornitore} on={(v) => setForm({ ...form, fornitore: v })} testid="p-fornitore" />
              <Field label="Quantità magazzino virtuale" type="number" val={form.quantita_virtuale} on={(v) => setForm({ ...form, quantita_virtuale: v })} testid="p-qv" />
              <Field label="Quantità magazzino fisico" type="number" val={form.quantita_fisica} on={(v) => setForm({ ...form, quantita_fisica: v })} testid="p-qf" />
              <Field label="Scorta minima (avviso sotto scorta)" type="number" step="0.01" val={form.scorta_minima} on={(v) => setForm({ ...form, scorta_minima: v })} testid="p-scorta" />
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Descrizione</label>
                <textarea rows={2} value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }} />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Commesse associate</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {commesse.length === 0 && <span className="text-xs" style={{ color: "var(--text-muted)" }}>Nessuna commessa</span>}
                  {commesse.map((c) => {
                    const active = form.commessa_ids.includes(c.id);
                    return (
                      <button key={c.id} type="button" onClick={() => toggleCommessa(c.id)}
                        className="px-3 py-1.5 rounded-full text-xs font-medium transition-all border"
                        style={active ? { background: "var(--primary)", color: "#fff", borderColor: "var(--primary)" } : { background: "#fff", borderColor: "var(--border)" }}>
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="md:col-span-2 flex justify-end gap-2">
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Annulla</button>
                <button type="submit" className="btn-primary" data-testid="p-submit">{editing ? "Aggiorna" : "Crea"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ExcelImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={() => load()}
        resource="prodotti"
      />
    </div>
  );
}

function Field({ label, val, on, req, type = "text", step, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</label>
      <input data-testid={testid} required={!!req} type={type} step={step} value={val ?? ""} onChange={(e) => on(e.target.value)}
        className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
        style={{ borderColor: "var(--border)", background: "#fff" }} />
    </div>
  );
}
